import { useFeatureFlag, FeatureFlagControlPanelProvider, AppScopeEnum } from '@newfront-insurance/admin-ui';
import { CoverageDslProvider } from '@newfront-insurance/admin-ui/coverage';
import { FallbackSuspense } from '@newfront-insurance/admin-ui/layout';
import { ChatOverlayProviderV2 } from '@newfront-insurance/nova-core';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import type { ReactNode, JSX } from 'react';

import { AccountUIProvider } from './account-ui-api';
import { AdminUIProvider, useAppConfig } from './admin-ui';
import { AccountsCoverageApiProvider } from './coverage-api';
import { IncompleteUserErrorProvider } from './incomplete-user-error-provider';
import { PostLoginRedirectProvider } from './post-login-redirect';

import { AuthProvider } from '@/client/providers/auth';
import { SnowfallProvider } from '@/client/providers/snowfall-provider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (_error, query) => {
        return !query.state.data && (query.options as QueryObserverOptions).suspense === true;
      },
    },
  },
});

interface Props {
  children: ReactNode;
}

export function ReactQueryProvider({ children }: Props): JSX.Element {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

interface ProvidersProps extends Props {
  /**
   * Whether the Nova chat overlay (and its floating button) should be mounted for the current
   * page. Defaults to true. Pages can opt-out by setting `MyPage.showsNovaChatOverlay = false`,
   * which is read in `_app.tsx` and forwarded here (mirrors the `requiresAuth` pattern).
   */
  showsNovaChatOverlay?: boolean;
}

/**
 * Compose all providers together into a single component
 */
export function Providers({ children, showsNovaChatOverlay = true }: ProvidersProps): JSX.Element {
  const isShowingDarkSidebar = useFeatureFlag('dark-navigator-sidebar', false);
  const appConfig = useAppConfig();

  const wrappedChildren = showsNovaChatOverlay ? (
    // Hide the floating button whenever we do not render account app layout (ie. appConfig is undefined)
    <ChatOverlayProviderV2 hostApp="accounts" authProvider={AuthProvider} transparentFloatingButton={!appConfig}>
      <SnowfallProvider>{children}</SnowfallProvider>
    </ChatOverlayProviderV2>
  ) : (
    <SnowfallProvider>{children}</SnowfallProvider>
  );

  return (
    <FallbackSuspense>
      <IncompleteUserErrorProvider>
        <AccountUIProvider>
          <AdminUIProvider isShowingDarkSidebar={isShowingDarkSidebar}>
            <PostLoginRedirectProvider>
              <FeatureFlagControlPanelProvider appScope={AppScopeEnum.ADMIN_NAVIGATOR} authProvider={AuthProvider}>
                <AccountsCoverageApiProvider>
                  <CoverageDslProvider>
                    <NuqsAdapter>{wrappedChildren}</NuqsAdapter>
                  </CoverageDslProvider>
                </AccountsCoverageApiProvider>
              </FeatureFlagControlPanelProvider>
            </PostLoginRedirectProvider>
          </AdminUIProvider>
        </AccountUIProvider>
      </IncompleteUserErrorProvider>
    </FallbackSuspense>
  );
}
